import React, {useEffect, useMemo, useState} from 'react';

import {useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';

import {ButtonVariant} from 'components/ui-kit/Button';
import {Select, SelectOption} from 'components/ui-kit/Select';
import {$isRemovingLock, $removeLockModalOpened, removeLockCode} from 'models/codeLocks';
import {UnblockingReason} from 'types/products-blocking';

import {mapEnumToI18nOptions} from 'utils/select';

import {
    Body,
    CommentField,
    Footer,
    FooterButton,
    StyledModal,
    Subtitle,
    Title,
    TitleBlock,
} from './styled';

interface Props {
    show: boolean;
    markingCode: string | undefined;
    onClose: () => void;
}

export const UnblockModal = ({show, markingCode, onClose}: Props) => {
    const {t} = useTranslation();
    const [reason, setReason] = useState<SelectOption<UnblockingReason> | null>(null);
    const [comment, setComment] = useState('');
    const isRemovingLock = useStore($isRemovingLock);
    const removeLockModalOpened = useStore($removeLockModalOpened);

    const reasonOptions = useMemo(
        () =>
            mapEnumToI18nOptions(
                UnblockingReason,
                'dictionaries.products.unblockingReason',
            ) as SelectOption<UnblockingReason>[],
        [t],
    );

    useEffect(() => {
        if (!show) {
            setReason(null);
            setComment('');
        }
    }, [show]);

    const handleConfirm = () => {
        if (!reason?.value || !removeLockModalOpened || !markingCode) return;

        const payload = {
            blockingId: removeLockModalOpened.blockingId,
            markingCode,
            unblockingReason: reason.value,
            comment,
        };

        removeLockCode(payload);
    };

    return (
        <StyledModal
            show={show}
            onClose={onClose}
            modalWidth={1}
            title={
                <TitleBlock>
                    <Title>{t('pages.products.detail.blocking.modal.title')}</Title>
                    <Subtitle>{t('pages.products.detail.blocking.modal.subtitle')}</Subtitle>
                </TitleBlock>
            }
        >
            <Body>
                <Select
                    label={t('pages.products.detail.blocking.modal.reason')}
                    isRequired
                    placeholder={t('pages.products.detail.blocking.modal.reasonPlaceholder')}
                    options={reasonOptions}
                    value={reason}
                    onChange={(option: SelectOption<UnblockingReason> | null) => setReason(option)}
                />
                <CommentField
                    updatedStyle
                    label={t('pages.products.detail.blocking.modal.comment')}
                    placeholder={t('pages.products.detail.blocking.modal.commentPlaceholder')}
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                />
            </Body>
            <Footer>
                <FooterButton type="button" variant={ButtonVariant.Outline} onClick={onClose}>
                    {t('pages.products.detail.blocking.modal.cancel')}
                </FooterButton>
                <FooterButton
                    type="button"
                    variant={ButtonVariant.Normal}
                    disabled={!reason?.value || isRemovingLock}
                    isLoading={isRemovingLock}
                    onClick={handleConfirm}
                >
                    {t('pages.products.detail.blocking.modal.confirm')}
                </FooterButton>
            </Footer>
        </StyledModal>
    );
};
